import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Dialog, Menu } from "react-aria-components";
import { MoreVertical, Copy, FolderInput, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import type { ManualState } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { availableManualTransitions, type ManualTransitionKey } from "./manual-transitions.js";

export interface ManualKebabMenuProps {
  manual: { id: number; name: string };
  /** The manual's lifecycle state — drives the review transitions offered at the TOP of the menu (ADR-018). */
  state: ManualState;
  /** An archived manual → the only actions are Unarchive + Papierkorb (no review/Duplicate/Move/Archive on it). */
  archived: boolean;
  /** Best-effort client gate (`manuals_manage`+ on the facility); the server is authoritative (state-dependent authz). */
  canManage: boolean;
  /** The caller has the facility **`confirm`** tier — reveals the publish/unpublish/clear-flag transitions (Confirm,
   *  Unmark, reopen a confirmed manual). Without it those are hidden (a `manuals_manage` caller would 403 — panel). */
  canConfirm: boolean;
  onDuplicate: () => void;
  onMove: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  /** Moves it to the trash (ADR-056). */
  onDelete: () => void;
  /** A review transition (ADR-018) — the caller maps the key to a mutation (or opens the mark-comment dialog). */
  /** Fire a review transition. **Optional since the ADR-018 addendum §3 sweep**: the manuals list no longer owns the
   *  review workflow in realms that can reach the editor, so the view passes this ONLY where it cannot (classic nav,
   *  which has no editor at all — see {@link FacilityManualsView}). Absent ⇒ no transition items. */
  onTransition?: (key: ManualTransitionKey) => void;
}

/**
 * The per-manual actions menu (⋮), mirroring {@link FacilityKebabMenu} — a Popover + Dialog (NOT a bare `role="menu"`)
 * so the static manual-name header is read by screen readers, with the actions in a separate `<Menu>` below. Active
 * cards lead with the state's review transition(s) (ADR-018 — Submit / Confirm / Mark… / Unmark / Return to draft), then
 * Duplicate · Move… · Archive · Papierkorb; archived manuals offer only Unarchive · Papierkorb (out of the review workflow).
 * `onAction` closes the popover first, then runs the handler. Papierkorb + Mark are the destructive variant. All gated by
 * `canManage` (server-authoritative).
 */
export function ManualKebabMenu({ manual, state, archived, canManage, canConfirm, onDuplicate, onMove, onArchive, onUnarchive, onDelete, onTransition }: ManualKebabMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Review transitions lead the menu (active manuals only — an archived manual is out of the workflow). Already filtered
  // to what the caller MAY do (tier-gated), so they render enabled; the lifecycle group below stays shown-but-disabled.
  // Only when this surface OWNS the workflow (no reachable editor). Where the editor exists, review status changes
  // live solely in its status control — a Confirm item here would walk straight past the Check Manual gate.
  const transitions = archived || !onTransition ? [] : availableManualTransitions(state, { canManage, canConfirm });
  const actions: Record<string, () => void> = {
    duplicate: onDuplicate,
    move: onMove,
    archive: onArchive,
    unarchive: onUnarchive,
    delete: onDelete,
    ...Object.fromEntries(transitions.map((t) => [t.key, () => onTransition?.(t.key)])),
  };
  const disabledKeys = canManage
    ? new Set<string>()
    : new Set<string>(["duplicate", "move", "archive", "unarchive", "delete"]);

  return (
    <PopoverTrigger isOpen={open} onOpenChange={setOpen}>
      <Button variant="ghost" size="icon-sm" aria-label={t("manualManagement.kebab.actions", { name: manual.name })}>
        <MoreVertical className="text-muted-foreground size-4" />
      </Button>

      <Popover className="w-52 gap-0 p-0" placement="bottom end">
        <Dialog aria-label={t("manualManagement.kebab.actions", { name: manual.name })} className="outline-none">
          <div className="border-border truncate border-b px-2.5 py-2 text-sm font-medium">{manual.name}</div>

          <Menu
            aria-label={t("manualManagement.kebab.actionsAlt", { name: manual.name })}
            className="flex flex-col p-1 outline-none"
            disabledKeys={disabledKeys}
            onAction={(key) => {
              setOpen(false);
              actions[String(key)]?.();
            }}
          >
            {transitions.length > 0 ? (
              <>
                {transitions.map((tr) => {
                  const Icon = tr.icon;
                  return (
                    <DropdownMenuItem key={tr.key} id={tr.key} textValue={t(tr.labelKey)} variant={tr.destructive ? "destructive" : "default"}>
                      <Icon />
                      {t(tr.labelKey)}
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
              </>
            ) : null}

            {archived ? (
              <DropdownMenuItem id="unarchive" textValue={t("manualManagement.kebab.unarchive")}>
                <ArchiveRestore />
                {t("manualManagement.kebab.unarchive")}
              </DropdownMenuItem>
            ) : (
              <>
                <DropdownMenuItem id="duplicate" textValue={t("other.duplicate")}>
                  <Copy />
                  {t("other.duplicate")}
                </DropdownMenuItem>
                <DropdownMenuItem id="move" textValue={t("manualManagement.kebab.moveTo")}>
                  <FolderInput />
                  {t("manualManagement.kebab.moveTo")}
                </DropdownMenuItem>
                <DropdownMenuItem id="archive" textValue={t("facilityManagement.actions.archive")}>
                  <Archive />
                  {t("facilityManagement.actions.archive")}
                </DropdownMenuItem>
              </>
            )}

            <DropdownMenuSeparator />

            <DropdownMenuItem id="delete" textValue={t("manualManagement.kebab.trash")} variant="destructive">
              <Trash2 />
              {t("manualManagement.kebab.trash")}
            </DropdownMenuItem>
          </Menu>
        </Dialog>
      </Popover>
    </PopoverTrigger>
  );
}
